/**
 * Spreadsheet Helpers
 * @fileoverview Helper functions to manipulate Handsontable aspects
 */

import type Handsontable from 'handsontable';
import isNil from 'lodash/isNil';
import numbro from 'numbro';
import { defaultColumnSettings } from './constants';

import {
  DataType,
  HandsontableInstanceType,
  SpreadsheetColumnType,
  CellValueType,
  CellValidatorCallbackType,
  DataStateManagerConfigType,
  CellUpdateType,
  AfterChangeCallbackType,
} from './types';
import type { SpreadsheetV2Props } from './SpreadsheetV2';
/**
 * Align Handsontable headers to left or right based on column type
 * @param columns Array of configuration objects coming from props
 */
export function alignColumns(columns: SpreadsheetColumnType[]) {
  return (col: number, th: HTMLTableCellElement) => {
    const column = columns[col];

    if (!column) {
      // The first time this function runs, the value passed to `col` is -1, indicating the top
      // left corner empty cell. We don't care about the alignment of the content in that cell
      // since there's never anything there.
      return;
    }

    if (['numeric', 'currency', 'percentage'].includes(column.type)) {
      th.querySelector('.relative')?.classList.add('htRight');
    } else if (['text'].includes(column.type) && column.align === 'right') {
      th.querySelector('.relative')?.classList.add('htRight');
    } else {
      th.querySelector('.relative')?.classList.add('htLeft');
    }
  };
}

/**
 * Gets column index when identifier is a string in data objects
 * @param columns Array of configuration objects coming from props
 * @param prop Column identifier as a string when data is an object and a number when data is an array
 * @return The numerical index of a column matching the prop argument
 */
export function getColumnIndex(columns: SpreadsheetColumnType[], prop: string | number): number {
  if (typeof prop !== 'string') {
    return prop;
  }

  return columns.findIndex(col => col.data === prop);
}

/**
 * Gets error message set in column
 * @param columns Array of configuration objects coming from props
 * @param col Index of column
 * @return The numerical index of a column matching the prop argument
 */
export function getErrorMessage(columns: SpreadsheetColumnType[], col: number) {
  const column = columns[col];
  const columnType = column?.type || 'text';

  const errorMessage = column?.validation?.errorMessage;
  const defaultErrorMessage = defaultColumnSettings[columnType].validation.errorMessage;
  return errorMessage || defaultErrorMessage;
}

/**
 * Run Handsontable validation only in rows that have non-null data
 * or rows that have invalid cells
 * @description This function will run validation
 * only for rows that have any non-null data or invalid cells in them.
 * Example:
 * ```
 *  const data = [{name: 'Tagg', shares: null}, {name: null, shares: null}];
 *  validateRowsWithData(data) -> Will run validation for the first column but not the second;
 * ```
 * The goal is to preserve performance by validating only rows that have some data in them.
 * @param data Initial data object
 * @param hotInstance Current instance of Handsontable
 * @param callback Callback function to run after validation
 */
export function validateRowsWithData(
  data: DataType,
  hotInstance: HandsontableInstanceType,
  callback?: (valid: boolean) => void,
) {
  if (!data || !data.length) return;

  const rowsToValidate = data
    .map((_, i) => {
      if (hotInstance?.getCellMetaAtRow(i).some(cell => cell['valid'] === false)) return i;
      if (!hotInstance?.isEmptyRow(i)) return i;
      return undefined;
    })
    .filter(i => i !== undefined);

  if (rowsToValidate) {
    hotInstance?.validateRows(rowsToValidate as number[], callback);
  }
}

/**
 * Structures CellChanges object from HandsonTable into a readable format
 * @param changes CellChanges object from HandsonTable, representing all changed cells
 * @return Objects representing the changes to all edited cells
 */
export const structureChanges = (changes: Handsontable.CellChange[] | null) => {
  if (!changes) {
    return [];
  }

  return changes.map(change => {
    const [rowIndex, column, prevValue, nextValue] = change;

    return {
      rowIndex,
      column,
      prevValue,
      nextValue,
    };
  });
};

/**
 * Calculates the horizonal center of the spreadsheet in order to determine where an error
 * tooltip should render.
 * @param componentDiv The parent div of the HOT instance
 * @return A number representing the center pixel value of the spreadsheet
 */
export const calculateHorizontalArea = (componentDiv: HTMLElement) => {
  const tableArea = componentDiv.getBoundingClientRect();
  const tableCenter = tableArea.width / 2 + tableArea.left;
  return tableCenter || 0;
};

/**
 * Returns a custom validator Handsontable uses to validate cells.
 *
 * Handsontable passes a value and a callback to this function and expects that 'callback' gets
 * called with 'true' or 'false'. What actually gets returned does not matter for Handsontable
 * validation.
 *
 * There are two circumstances in which a cell requires validation:
 * 1. It is required
 * 2. It has a validator. This can either be custom validation from the column config, or the
 * built-in column type validation from Handsontable (for example, "numeric" type columns have
 * validation to verify that the value is a number)
 *
 * This function returns early if neither of the above conditions are met.
 *
 * It also returns early if the cell value is null or an empty string. This may happen if a user clicks
 * into a required cell, then presses enter without typing anything. In this case, we return a truthy
 * validity, as not to trigger an error state when there is no value in the cell.
 *
 * Otherwise, it will determine validity based on the validator. If the validator is a function, it
 * calls that function with the cell value. If the validator is a regex, it evaluates the regex
 * based on the cell value. Then, it returns the callback from Handsontable with the validity boolean
 * as an argument.
 *
 * @param columnProps Object that defines a column configuration
 */
export const getValidator = (
  columnProps: SpreadsheetColumnType,
  hotInstanceValidators: typeof Handsontable.validators,
) => {
  const validator = columnProps.validation?.callback;

  // Resolves different types of validator (RegExp or function).
  const resolveValidator = (value: CellValueType, callback: CellValidatorCallbackType, cellCoordinates) => {
    if (validator instanceof RegExp) {
      const isValid = validator.test(value as string);
      return callback(isValid);
    }
    if (validator) return callback(Boolean(validator(value, cellCoordinates)));
  };

  // Handsontable will validate columns that have `allowEmpty: false`.
  // However, it only happens if the cell has a validator function defined.
  // Text type columns do not have default validators, so validation will not
  // run unless we define a validator for it. Since text columns do not have
  // default validators, they will always be valid unless the user defines one.
  if (columnProps.type === 'text' && columnProps.required) {
    return function hotCallback(value: CellValueType, callback: CellValidatorCallbackType) {
      const { row, col } = this;
      if (!value) return callback(false);
      if (!validator) return callback(true);
      return resolveValidator(value, callback, { row, col });
    };
  }

  // If a column that is not text has a user defined validator,
  // Handsontable will use the internal validator for required first,
  // then the user defined validator provided the cell does not have an empty value ("" counts as empty).
  if (validator)
    return function hotCallback(value: CellValueType, callback: CellValidatorCallbackType) {
      const { row, col } = this;
      resolveValidator(value, callback, { row, col });
    };

  // If none of the above are true, we pass in Handsontable specific column validators
  const typeValidator = defaultColumnSettings[columnProps.type]['validator'];

  if (typeof typeValidator === 'string' && typeValidator in hotInstanceValidators) {
    return hotInstanceValidators[typeValidator];
  }
};

/**
 * Determine the indices of recently changed rows that are now entirely empty
 *
 * @param hotInstance Current instance of Handsontable
 * @param changes CellChanges object from HandsonTable, representing all changed cells
 * @returns An array with the indices of all newly empty rows
 */
export const getNewlyEmptyRows = (hotInstance: Handsontable, changes: Handsontable.CellChange[] | null) => {
  if (!changes) return [];
  return [...new Set(changes.map(change => change[0]))].filter(hotInstance?.isEmptyRow);
};

type InjectErrorCellMessage = (
  columns: SpreadsheetColumnType[],
  hotInstance: HandsontableInstanceType,
) => Handsontable.GridSettings['afterValidate'];
/**
 * Adds a data-error attribute containing an error message to invalid cells
 * tooltip should render.
 *
 * Note: the hook useCustomSpreadsheetErrors also interacts with this code.
 *
 * @param columns The columns array coming from props
 * @param hotInstance Current instance of Handsontable
 */
export const injectCellErrorMessage: InjectErrorCellMessage = (columns, hotInstance) => (isValid, value, row, prop) => {
  const meta = hotInstance?.getCellMeta(row, getColumnIndex(columns, prop));
  const col = getColumnIndex(columns, prop);
  const td = hotInstance?.getCell(row, col, true);

  if (!td) {
    return;
  }

  if (isValid) {
    /*
      If the cell passed internal validation, we still need to keep external errors;
      We're comparing with `true` explicitly because that's less work than checking
      for `false` and `undefined`.
    */
    const hasExternalError = meta?.hasExternalError === true;
    if (hasExternalError) {
      td.dataset.error = meta?.externalErrorMessage;
      return false; // to keep the cell invalid even though it passed internal validation
    }

    td.removeAttribute('data-error');
    return isValid;
  }

  /*
    From this point, we know isValid === false.
  */

  const isRequiredColumn = columns[col]?.required;
  const onlyValueOnRow = hotInstance?.getDataAtRow(row).filter((cell, i) => i !== col && cell !== null).length === 0;
  const isEmptyRow = hotInstance?.isEmptyRow(row);
  const isEmptyValue = value === null || value === '';

  /*
    Deciding what to do when the cell is empty.
  */
  if (isEmptyValue) {
    if (meta?.readOnly) {
      /* If the cell is a readOnly cell it doesn't need an error state. */
      td.removeAttribute('data-error');
      return true;
    }

    if (isEmptyRow) {
      /* If the entire row is empty, we don't want to show an error message. */
      td.removeAttribute('data-error');
      return true;
    }

    if (onlyValueOnRow) {
      /* If the cell is the only value on the row, we don't want to show an error message. */
      td.removeAttribute('data-error');
      return true;
    }

    if (isRequiredColumn) {
      /* If the column is required, we want to show a different error message. */
      td.dataset.error = 'This field is required';
      return false;
    }
  }

  /*
    Finally, setting the error message.
  */
  td.dataset.error = getErrorMessage(columns, col);

  return isValid;
};

type DefineTooltipPosition = (handsontable: Handsontable) => Handsontable.GridSettings['beforeOnCellMouseOver'];
/**
 * Sets the tooltip to open left or right depending on cell position
 * from the vertical center of the spreadsheet
 * @param ref Ref object with current instance of SpreadsheetV2
 */
export const defineTooltipPosition: DefineTooltipPosition = (handsontable: Handsontable) => (
  event,
  coords,
  TD,
  controller,
) => {
  if (!TD.classList.contains('invalid-cell')) {
    return;
  }
  // remove all error-tooltip classes to avoid multiple classes being added
  TD.classList.remove('error-tooltip__bottom', 'error-tooltip__left', 'error-tooltip__right');

  const columnsLength = handsontable.countCols() - 1; // number of columns - 1 to compare with column coordinates.
  const tableXCenter = calculateHorizontalArea(handsontable.rootElement);
  const mouseX = event.clientX; // mouse X coordinate

  const spreadsheetTableRect = handsontable.rootElement.getBoundingClientRect();
  const spreadsheetTableBottom = spreadsheetTableRect.bottom;
  const tDRect = TD.getBoundingClientRect();
  const errorToolTipStyle = window.getComputedStyle(TD, ':after');
  // the value here is in respect to the parent cell i.e. TD
  const errorTooltipLocalBottom = Math.abs(parseInt(errorToolTipStyle.getPropertyValue('bottom'), 10));
  const errorTooltipBottom = tDRect.top + errorTooltipLocalBottom;
  // this is a buffer space to avoid the error tooltip from being rendered out of bounds
  const bufferSpace = 30;
  /*
  For the first and last columns, we always want to render the tooltip
  on the opposite side (right for the first column, left for the last)
  to avoid rendering out of bounds. For all other columns, we render
  it on the side of the cell that's closest to the center of the screen.
*/
  if (coords.col === 0) {
    TD.classList.add('error-tooltip__right');
  } else if (coords.col === columnsLength) {
    TD.classList.add('error-tooltip__left');
  } else if (mouseX < tableXCenter) {
    // we also need to remove the last contrary positioning otherwise the error won't change sides when the mouse is moved.
    TD.classList.remove('error-tooltip__left');
    TD.classList.add('error-tooltip__right');
  } else {
    TD.classList.remove('error-tooltip__right');
    TD.classList.add('error-tooltip__left');
  }
  if (spreadsheetTableBottom < errorTooltipBottom + bufferSpace) {
    TD.classList.add('error-tooltip__bottom');
  }
};

type UnformatNumbers = (
  changes: (Handsontable.CellChange | null)[],
  columns: SpreadsheetV2Props['columns'],
) => boolean | void;

/**
 * Unformats numbers as soon as they are entered in the spreadsheet to avoid
 * formatting numbers and currencies incorrectly as Handsontable will interpret
 * numbers like 1,500 as 1.5 and not 1500.
 *
 * @param changes Changes array returned by `beforeChanges` hook
 */
export const unformatNumbers: UnformatNumbers = (changes, columns) => {
  changes.forEach(change => {
    if (!change) return;

    const prop = change[1];
    let currentValue = change[3];
    const column = columns[getColumnIndex(columns, prop)];

    if (!column) return;
    const isMatchingData = prop === column.data;
    const isNumericOrCurrencyColumn = ['numeric', 'currency'].includes(column.type);

    // checks to see if the column where the change is is numeric or currency
    if (!isMatchingData || !isNumericOrCurrencyColumn) {
      return;
    }

    // runs unformatting only on the numberic and currency columns

    /**
     * we need this because string inputs containing letters were being passed into
     * unformat and returning undefined, which were removing any inputs that were
     * not numbers and failing proper validation
     *
     * This tests each input against the allowed numbers or symbols for it to be
     * considered a number
     */

    const containsLettersOrSymbols = String(currentValue).match(/[^$0-9.,]/);

    if (!containsLettersOrSymbols) {
      // if user passes incorrect value that passes the regex test, its better to return the original value to the user instead of undefined eg - $$12.12 => $$12.12
      currentValue = numbro.unformat(currentValue) ? numbro.unformat(currentValue) : currentValue;
    }

    // eslint-disable-next-line no-param-reassign
    change[3] = currentValue;
  });
};

type TrimClipboardWhitespace = (data: any[][], config: { collapseClipboardWhitespace?: boolean }) => void;

/**
 * When users paste data into the spreadsheet that comes from MS Word or
 * MS Excel, this data will contain whitespace that will cause validation
 * to fail, especially for "dropdown" and "typeahead" columns. This function
 * iterates over each cell in the clipboard data and trims whitespace from
 * the beginning and end of each cell.
 *
 * It's important to note that this function is supposed to edit the clipboard
 * data IN-PLACE.
 *
 * @param data Array of clipboard data
 * @param config Optionally trim internal whitespace (e.g. "Hello\n   World" -> "Hello World")
 */
export const trimClipboardWhitespace: TrimClipboardWhitespace = (data, config) => {
  data.forEach((row, rowIndex) => {
    row.forEach((value, colIndex) => {
      // This should never happen, but since these arrays are typed as any[][]
      // from the Handsontable side, it feels safer to check.
      if (typeof value !== 'string') return;

      let alteredValue = value.trim();

      if (config.collapseClipboardWhitespace) {
        alteredValue = alteredValue.replace(/\s+/g, ' ');
      }
      // eslint-disable-next-line no-param-reassign
      data[rowIndex][colIndex] = alteredValue;
    });
  });
};

type IgnoreEmptyChanges = (hotInstance: HandsontableInstanceType) => Handsontable.GridSettings['beforeChange'];

/**
 * Ignore validation when clicking in and out of a cell in rows that are empty.
 *
 * Handsontable changes the value of an empty cell on focus which causes validation to run.
 * If a cell is required, clicking in and out of an empty cell without typing makes the cell invalid,
 * which is not a desired behavior at this point.
 * @param hotInstance Current instance of Handsontable
 */
export const ignoreEmptyChanges: IgnoreEmptyChanges = hotInstance => (changes, source) => {
  if (source !== 'edit') return;

  // Grabs 'row' and 'value' of the first change,
  // from the changes array coming from Handsontable.
  // Since the source of the change can only be edit,
  // there will only be one change at a time.
  const firstChange = changes[0]!;
  const row = firstChange[0];
  const value = firstChange[3];

  const isEmptyRow = hotInstance?.isEmptyRow(row);

  if (isEmptyRow && value === '') return false;

  return undefined;
};

/**
 * Callback which allows users to manage the Spreadsheet's state.
 *
 * The config object includes the following params:
 * stateSetter: A user-defined method for setting state
 * extraChanges?: {
 *    rowIndex: the index of the row to which you want to apply changes.
 *    extraChangesRow: the rest of the values to auto-fill into the row, structured as an object.
 * }
 * extraChanges is an optional param that is only used when the spreadsheet will make additional changes
 * beyond the changes the user just made. For example, if the user fills in a cell in the "name" column,
 * and the rest of the columns auto-fill based on that value.
 *
 * See samples.js for examples
 * @param config Configuration object which determines which changes need to take place.
 */
export const dataStateManager = (data: Handsontable.RowObject[], hotInstance: HandsontableInstanceType) => (
  config: DataStateManagerConfigType,
) => {
  if (!config.extraChanges) {
    config.stateSetter(data);
    return;
  }

  const { index, rowChanges: changes } = config.extraChanges;
  const cellsToUpdate: Array<CellUpdateType> = Object.entries(changes).map(([field, value]) => [index, field, value]);

  /* Merge existing data with provided extraChanges */
  const newData = [...data!];
  newData[index] = { ...(newData[index] ?? {}), ...changes };

  hotInstance?.setSourceDataAtCell(cellsToUpdate);
  config.stateSetter(newData);
};

/**
 * Returns a function to be called with changes injected in the `afterChange` callback.
 *
 * @param changes An array of objects representing changes that triggered this handler
 */
export const changeHandler = (data: DataType, hotInstance: Handsontable, afterChange: AfterChangeCallbackType) => (
  changes: Handsontable.CellChange[] | null,
) => {
  if (isNil(data)) {
    return;
  }

  const newData = [...data!];
  changes &&
    changes.forEach(change => {
      const [row, col, currentValue, newValue] = change;

      if (currentValue !== newValue) {
        newData[row] = {
          ...newData[row],
          [col]: newValue,
        };
      }
    });

  const changesArray = structureChanges(changes);

  const changesObject = changesArray.length ? changesArray[0] : {};

  return afterChange({
    // NOTE: Regardless of whether `changes` is null, we should make sure `dataStateManager` gets called.
    dataStateManager: dataStateManager(newData, hotInstance),
    changes: changesObject,
    changesArray,
  });
};
